import React, { useEffect, useRef, useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { CircularProgress, MenuItem, Pagination, TextField } from '@mui/material';
import './ProductSearch.css';
import { OutlinedInputField } from './pages/components/OutlinedInputField';

const SEARCH_CATALOG = gql`
  query SearchCatalogItems($keyword: String!, $page: Int!, $pageSize: Int!) {
    searchCatalogItems(keyword: $keyword, page: $page, pageSize: $pageSize) {
      totalCount
      items {
        item
        thumbnailUrl
        models {
          airtableRecordId
          mongoScrapeId
          model
          displayName
          thumbnailUrl
          numberOfTransactions
          cashPrice
          storeCreditPrice
          requiresReview
        }
      }
    }
  }
`;

export function selectedProduct(item, model) {
  return {
    ...model,
    id: model.airtableRecordId,
    product: model.displayName,
    itemName: item.item,
    modelName: model.model,
    catalogVersion: 2,
  };
}

export function selectionStep(item) {
  if (!item || !Array.isArray(item.models) || item.models.length === 0) return { type: 'unavailable' };
  if (item.models.length === 1) return { type: 'select', product: selectedProduct(item, item.models[0]) };
  return { type: 'choose-model', item };
}

export default function ProductSearch({ onSelect, ...otherProps }) {
  const pageSize = 5;
  const client = useApolloClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [committedTerm, setCommittedTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pendingItem, setPendingItem] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const debounceTimer = useRef(null);
  const activeController = useRef(null);
  const requestSequence = useRef(0);
  const trimmedTerm = searchTerm.trim();
  const resultsMatchInput = Boolean(committedTerm && trimmedTerm === committedTerm);
  const items = resultsMatchInput ? (data?.searchCatalogItems?.items || []) : [];
  const totalCount = resultsMatchInput ? (data?.searchCatalogItems?.totalCount || 0) : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    activeController.current?.abort();
    requestSequence.current += 1;

    if (!trimmedTerm) {
      setCommittedTerm('');
      setData(null);
      setLoading(false);
      setSearchError(false);
      return undefined;
    }

    setLoading(true);
    setSearchError(false);
    debounceTimer.current = setTimeout(() => setCommittedTerm(trimmedTerm), 300);
    return () => clearTimeout(debounceTimer.current);
  }, [trimmedTerm]);

  useEffect(() => {
    if (!committedTerm) return undefined;

    const controller = new AbortController();
    const sequence = ++requestSequence.current;
    activeController.current = controller;
    setLoading(true);
    setSearchError(false);

    client.query({
      query: SEARCH_CATALOG,
      variables: { keyword: committedTerm, page, pageSize },
      fetchPolicy: 'no-cache',
      context: {
        queryDeduplication: false,
        fetchOptions: { signal: controller.signal },
      },
    }).then((result) => {
      if (sequence === requestSequence.current && !controller.signal.aborted) {
        setData(result.data);
      }
    }).catch((error) => {
      if (sequence === requestSequence.current && error?.name !== 'AbortError') {
        setData(null);
        setSearchError(true);
      }
    }).finally(() => {
      if (sequence === requestSequence.current) setLoading(false);
    });

    return () => {
      controller.abort();
      if (activeController.current === controller) activeController.current = null;
      requestSequence.current += 1;
    };
  }, [client, committedTerm, page]);

  const resetSearch = () => {
    clearTimeout(debounceTimer.current);
    activeController.current?.abort();
    activeController.current = null;
    requestSequence.current += 1;
    setSearchTerm('');
    setCommittedTerm('');
    setData(null);
    setLoading(false);
    setSearchError(false);
    setPage(1);
    setPendingItem(null);
  };

  const chooseItem = (item) => {
    const step = selectionStep(item);
    if (step.type === 'select') {
      onSelect(step.product);
      resetSearch();
      return;
    }
    if (step.type === 'choose-model') setPendingItem(item);
  };

  const chooseModel = (recordId) => {
    const model = pendingItem.models.find((candidate) => candidate.airtableRecordId === recordId);
    if (!model) return;
    onSelect(selectedProduct(pendingItem, model));
    resetSearch();
  };

  return (
    <div className="product-search-container" {...otherProps}>
      <OutlinedInputField
        label="Select your item"
        value={searchTerm}
        onChange={(event) => {
          const value = event.target.value;
          setSearchTerm(value);
          setPage(1);
          setPendingItem(null);
        }}
        fullWidth
        autoComplete="off"
      />

      {pendingItem && (
        <TextField
          select
          fullWidth
          className="model-select"
          label={`Select model for ${pendingItem.item}`}
          value=""
          onChange={(event) => chooseModel(event.target.value)}
        >
          {pendingItem.models.map((model) => (
            <MenuItem key={model.airtableRecordId} value={model.airtableRecordId}>
              {model.model}
            </MenuItem>
          ))}
        </TextField>
      )}

      {searchTerm && !pendingItem && (
        <div className="product-search-results">
          {loading && items.length === 0 ? (
            <div className="product-search-loading"><CircularProgress size={24} /></div>
          ) : searchError ? (
            <div className="product-search-empty">Search is temporarily unavailable. Please try again.</div>
          ) : items.length ? items.map((item) => (
            <button key={item.item} type="button" className="product-search-result-item" onClick={() => chooseItem(item)}>
              <img src={item.thumbnailUrl} alt="" />
              <span>{item.item}</span>
              <small>{item.models.length} {item.models.length === 1 ? 'model' : 'models'}</small>
            </button>
          )) : <div className="product-search-empty">No items found.</div>}
          {totalCount > pageSize && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, nextPage) => setPage(nextPage)}
              shape="rounded"
            />
          )}
        </div>
      )}
    </div>
  );
}
